import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';
import {Router} from '@angular/router';
import {User} from '../_model';
import {EndPointService} from './end.point.service';
import {LocalStorageService} from './index';
import {environment} from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public currentUser: Observable<User>;
  public currentUserProfile: Observable<User>;
  private currentUserSubject: BehaviorSubject<User>;

  private currentUserProfileSubject: BehaviorSubject<User>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private endpointsService: EndPointService,
    private localStorageService: LocalStorageService) {
    this.currentUserSubject = new BehaviorSubject<User>(
      this.localStorageService.get(environment.currentUser)
    );

    this.currentUserProfileSubject = new BehaviorSubject<User>(
      this.localStorageService.get(environment.userProfile)
    );
    this.currentUser = this.currentUserSubject.asObservable();

    this.currentUserProfile = this.currentUserProfileSubject.asObservable();
  }

  /**
   * Get current user token value
   * return {User}
   */
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   * Get current user profile
   * return {profile}
   */
  public get currentUserProfileValue(): User {
    return this.currentUserProfileSubject.value;
  }

  /**
   * Call login api
   * param data {email,password}
   * return {Observable<any>}
   */
  login(data) {
    return this.http.post<any>(this.endpointsService.login, {data}).pipe(
      map((result) => {
        const user = result.data;

        // login successful if there's a jwt token in the response
        if (user && user.token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          this.localStorageService.set(environment.currentUser, user);
          this.currentUserSubject.next(user);
        }
        return user;
      })
    );
  }

  /**
   * Forgot Password api
   * param data {email}
   * return {Observable<any>}
   */
  forgotPassword(data) {
    return this.http.post<any>(this.endpointsService.forgotPassword, {data}).pipe(
      map((result) => result));
  }

  /**
   * Reset Password api
   * return {Observable<any>}
   */
  resetPassword(data) {
    return this.http.post<any>(this.endpointsService.resetPassword, {data}).pipe(
      map((result) => result));
  }

  /**
   * Activate Account api
   * return {Observable<any>}
   */
  activateAccount(userId, token) {
    return this.http.get<any>(this.endpointsService.activateAccount + '/' + token + '/userId/' + userId).pipe(
      map((result) => result));
  }

  /**
   * ReSend OTP api
   * return {Observable<any>}
   */
  resendCode(email) {
    return this.http.get<any>(this.endpointsService.resendOtp + email).pipe(
      map((result) => result));
  }

  /**
   * @SRI
   * Call register api
   * param data {name,email,password}
   * return {Observable<any>}
   */
  register(data) {
    return this.http.post<any>(this.endpointsService.register, data).pipe(
      map((result) => result.data)
    );
  }

  /**
   * @Sri
   * Log out of the app
   * by clearing the local storage
   */
  logout() {
    // remove user from local storage to log user out
    // localStorage.removeItem('currentUser');
    this.localStorageService.delete(environment.currentUser);
    this.currentUserSubject.next(undefined);
    this.router.navigate(['./on-boarding']);
  }

  // get my profile
  getMyProfile() {
    return this.http.get<any>(this.endpointsService.getMyProfile).pipe(
      map((result) => result));
  }

  deleteMyAccount() {
    return this.http.get<any>(this.endpointsService.deleteMyAccount).pipe(
      map((result) => result));
  }


  /**
   * @SRI
   * Call update my profile api
   * param data {name,email,password}
   * return {Observable<any>}
   */
  updateMyProfile(data) {
    return this.http.put<any>(this.endpointsService.getMyProfile, data).pipe(
      map((result) => result.data)
    );
  }

  createEmotionReport(data) {
    return this.http.post<any>(this.endpointsService.emotionReport, {data}).pipe(
      map((result) => result.data));
  }

  updateEmotionReport(data, reportId) {
    return this.http.put<any>(this.endpointsService.emotionReport + '/' + reportId, {data}).pipe(
      map((result) => result.data));
  }

  getMyEmotionReport() {
    return this.http.get<any>(this.endpointsService.emotionReport + '/history').pipe(
      map((result) => result.data));
  }

  addFeedback(data) {
    return this.http.post<any>(this.endpointsService.feedback, {data}).pipe(
      map((result) => result.data));
  }

  getMyEmotionReportById(id) {
    return this.http.get<any>(this.endpointsService.emotionReport + '/detail?id=' + id).pipe(
      map((result) => result));
  }

  /*groupReports(title, address, location, projectType) {
    return this.http.get<any>(this.endpointsService.emotionReport +
      '/group?title=' + title + '&address=' + address + '&location=' + location + '&projectType=' + projectType).pipe(
      map((result) => result.data));
  }*/

  groupReports(title, address, location, projectType) {
    return this.http.put<any>(this.endpointsService.emotionReport + '/group/report', {
      title,
      address,
      location,
      projectType
    }).pipe(
      map((result) => result.data));
    /*return this.http.get<any>(this.endpointsService.emotionReport +
      '/group?title=' + title + '&address=' + address + '&location=' + location + '&projectType=' + projectType).pipe(
      map((result) => result.data));*/
  }

  getUsers({constraints}) {
    const url = environment.server + '/api/admin/users/list';
    return this.http.get<any>(url, {params: constraints}).pipe(
      map((result) => result));
  }

  getEmotionReport({constraints}) {
    const url = environment.server + '/api/admin/emotions';
    return this.http.get<any>(url, {params: constraints}).pipe(
      map((result) => result));
  }

  downloadEmotions({constraints}) {
    const url = environment.server + '/api/admin/download/emotions';
    return this.http.get<any>(url, {params: constraints}).pipe(
      map((result) => result));
  }

  downloadUsers({constraints}) {
    const url = environment.server + '/api/admin/download/users';
    return this.http.get<any>(url, {params: constraints}).pipe(
      map((result) => result));
  }


  getProjectData() {
    const url = environment.server + '/api/project/list/projects';
    return this.http.get<any>(url).pipe(
      map((result) => {
        this.localStorageService.set('project_list', result.data);
        return result.data;
      }));
  }

  getSurvey(projectType: any) {
    const url = environment.server + '/api/survey/' + projectType;
    return this.http.get<any>(url).pipe(
      map((result) => result.data));
  }

  getEvent(projectType: any) {
    const url = environment.server + '/api/emotion-reports/events-sites/' + projectType;
    return this.http.get<any>(url).pipe(
      map((result) => result.data));
  }

  createSite(data: any) {
    return this.http.post<any>(environment.server + '/api/site/create-site', {data})
      .pipe(map((result: any) => result));
  }

  getSiteCategory(projectId: any) {
    return this.http.get<any>(environment.server + '/api/site/list/category/' + projectId)
      .pipe(map((result: any) => result.data));
  }
}
